import React from 'react';

interface SkeletonProps {
  className?: string;
  width?: string | number;
  height?: string | number;
  variant?: 'text' | 'circular' | 'rectangular';
}

export const Skeleton: React.FC<SkeletonProps> = ({
  className = '',
  width,
  height,
  variant = 'rectangular',
}) => {
  // The fill: a soft light sweeping across (index.css, "Loading animations"), not a pulse.
  const baseClasses = 'cp-shimmer';
  
  const variantClasses = {
    text: 'rounded',
    circular: 'rounded-full',
    rectangular: 'rounded-md',
  };

  const style: React.CSSProperties = {
    width: width,
    height: height,
  };

  // A caller's own rounded-* REPLACES the variant's radius instead of joining it. Two
  // radius classes on one element are not settled by which is written last: Tailwind
  // emits them alphabetically, so the default variant's rounded-md came after — and beat
  // — a caller's rounded-full or rounded-lg, and every pill, dot and icon-button
  // placeholder was drawn with the wrong corners.
  const hasOwnRadius = className.split(/\s+/).some((c) => /^rounded(-|$)/.test(c));
  const radius = hasOwnRadius ? '' : variantClasses[variant];

  return (
    <div
      className={`${baseClasses} ${radius} ${className}`}
      style={style}
    />
  );
};

// Employee Table Skeleton
export const EmployeeTableSkeleton: React.FC = () => {
  return (
    <div className="h-full w-full">
      {/* Table Header */}
      <div className="sticky top-0 z-20 flex items-center gap-2 bg-gray-50/70 px-4 py-4 text-xs font-semibold text-gray-500">
        {['Employee Code', 'Employee Name', 'Joined Date', 'Official Email', 'Appointment Type', 'Employee Category', 'Budget Name', 'Project Name', 'City', 'Designation', 'Fixed CTC', 'Gratuity', 'Seed Score', 'Age on Network', 'Job Address', 'Pincode', 'Actions'].map((header, index) => (
          <div 
            key={header} 
            className={`whitespace-nowrap ${index === 16 ? 'sticky right-0 z-30 bg-gray-50/70 ml-auto' : ''}`}
            style={{ minWidth: index === 3 ? '200px' : index === 14 ? '180px' : '100px' }}
          >
            {header}
          </div>
        ))}
      </div>
      
      {/* Skeleton Rows */}
      <div className="divide-y divide-gray-100 bg-white">
        {Array.from({ length: 8 }).map((_, rowIndex) => (
          <div key={rowIndex} className="flex items-center gap-2 px-4 py-4">
            <Skeleton className="h-4 w-20 min-w-[80px]" />
            <Skeleton className="h-4 w-28 min-w-[112px]" />
            <Skeleton className="h-4 w-20 min-w-[80px]" />
            <Skeleton className="h-4 w-48 min-w-[192px]" />
            <Skeleton className="h-4 w-24 min-w-[96px]" />
            <Skeleton className="h-4 w-28 min-w-[112px]" />
            <Skeleton className="h-4 w-24 min-w-[96px]" />
            <Skeleton className="h-4 w-24 min-w-[96px]" />
            <Skeleton className="h-4 w-20 min-w-[80px]" />
            <Skeleton className="h-4 w-24 min-w-[96px]" />
            <Skeleton className="h-4 w-20 min-w-[80px]" />
            <Skeleton className="h-4 w-20 min-w-[80px]" />
            <Skeleton className="h-4 w-16 min-w-[64px]" />
            <Skeleton className="h-4 w-20 min-w-[80px]" />
            <Skeleton className="h-4 w-36 min-w-[144px]" />
            <Skeleton className="h-4 w-16 min-w-[64px]" />
            <div className="sticky right-0 z-10 flex items-center gap-2 bg-white pl-4">
              <Skeleton className="h-8 w-8 rounded-lg" />
              <Skeleton className="h-8 w-8 rounded-lg" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Skeleton;
